import { StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { sectionRenderer } from '@theme/section-renderer';

const configElement = document.getElementById('FwPairSavingsConfig');
const config = configElement ? JSON.parse(configElement.textContent) : null;

export function pairSavingsNote(cart, options) {
  const items = cart.items || [];
  const singles = new Set(options.handles);
  const qualifies = options.country === 'US' && cart.currency === 'USD' &&
    cart.item_count === 2 && items.length > 0 &&
    items.every(item => singles.has(item.handle || item.url?.split('/products/')[1]?.split('?')[0]) && !item.selling_plan_allocation &&
      item.original_price > 0 && item.original_price < options.threshold) &&
    cart.total_price >= options.threshold;
  if (!qualifies) return null;
  // Kit savings against regular kit prices; free shipping is stated in the note text.
  const separate = items.reduce((total, item) => total + item.original_line_price, 0);
  const savings = separate - cart.total_price;
  if (savings <= 0) return null;
  const amount = new Intl.NumberFormat(options.locale, {
    style: 'currency', currency: 'USD'
  }).format(savings / 100);
  return { key: items[0].key, text: options.template.replace('__amount__', amount) };
}

if (config) {
  const base = window.Shopify?.routes?.root || '/';
  const mutations = new Set();
  let wanted = false;
  let running = null;
  let checkoutBusy = false;
  const replay = new WeakSet();

  async function readCart() {
    const response = await fetch(base + 'cart.js', {
      headers: { Accept: 'application/json' }, cache: 'no-store'
    });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  async function synchronize() {
    await Promise.allSettled([...mutations, ...CartItemsComponent.pendingQuantityUpdates]);
    let cart = await readCart();
    let changed = false;
    for (let attempt = 0; attempt < 6; attempt++) {
      const note = pairSavingsNote(cart, config);
      const item = cart.items.find(line => {
        const expected = line.key === note?.key ? note.text : '';
        return (line.properties?.[config.label] || '') !== expected ||
          Boolean(line.properties?.[config.previousLabel]);
      });
      if (!item) break;
      const properties = { ...item.properties };
      if (properties[config.previousLabel]) properties[config.previousLabel] = '';
      properties[config.label] = item.key === note?.key ? note.text : '';
      const response = await fetch(base + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties })
      });
      if (!response.ok) throw new Error('Cart note unavailable');
      cart = await response.json();
      changed = true;
    }
    if (changed) {
      const sections = new Map();
      for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
        if (element.dataset.sectionId) {
          sections.set(element.dataset.sectionId, element.hasAttribute('data-drawer'));
        }
      }
      for (const [id, drawer] of sections) {
        await sectionRenderer.renderSection(id, {
          cache: false, mode: drawer ? 'hydration' : 'full', injectStylesheet: true
        });
      }
    }
  }

  function update() {
    wanted = true;
    if (running) return running;
    running = (async () => {
      while (wanted) {
        wanted = false;
        await synchronize();
      }
    })().finally(() => { running = null; });
    return running;
  }

  function backgroundUpdate() {
    update().catch(error => console.warn('[pair-savings]', error.message));
  }

  function cartChanged(event) {
    const mutation = Promise.resolve(event.promise);
    mutations.add(mutation);
    mutation.catch(() => {}).finally(() => {
      mutations.delete(mutation);
      backgroundUpdate();
    });
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, cartChanged);
  document.addEventListener(StandardEvents.cartDiscountUpdate, cartChanged);
  window.addEventListener('pageshow', event => {
    if (event.persisted) backgroundUpdate();
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form' ||
        !(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;
    if (replay.has(form)) {
      replay.delete(form);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      await update();
    } catch (error) {
      console.warn('[pair-savings]', error.message);
    } finally {
      checkoutBusy = false;
      const currentForm = document.getElementById('cart-form');
      const currentButton = document.querySelector('button[name="checkout"][form="cart-form"]');
      if (currentForm instanceof HTMLFormElement && currentButton instanceof HTMLButtonElement) {
        replay.add(currentForm);
        currentForm.requestSubmit(currentButton);
      }
    }
  }, true);
  if (config.initialRelevant) backgroundUpdate();
}
